/**
 * Service Worker Registration Utility
 * Handles registration and updates of the service worker
 */

interface Config {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

const isLocalhost = Boolean(
  globalThis.location.hostname === 'localhost' ||
  globalThis.location.hostname === '[::1]' ||
  /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/.exec(globalThis.location.hostname)
);

export function register(config?: Config) {
  // Only register in production and if supported
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL || '', globalThis.location.href);
    
    // Don't register if PUBLIC_URL is on different origin
    if (publicUrl.origin !== globalThis.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        
        navigator.serviceWorker.ready.then(() => {
          console.log(
            '[ServiceWorker] App is being served from cache by a service worker. ' +
            'Learn more: https://cra.link/PWA'
          );
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.log('[ServiceWorker] Registered successfully');
      
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New update available
              console.log('[ServiceWorker] New content available, please refresh.');
              
              if (config?.onUpdate) {
                config.onUpdate(registration);
              } else if (globalThis.confirm('New version available! Reload to update?')) {
                globalThis.location.reload();
              }
            } else {
              // Content cached for offline use
              console.log('[ServiceWorker] Content cached for offline use.');
              
              config?.onSuccess?.(registration);
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('[ServiceWorker] Registration failed:', error);
      
      config?.onError?.(error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      
      if (
        response.status === 404 ||
        (contentType != null && !contentType.includes('javascript'))
      ) {
        // Service worker not found, reload page
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            globalThis.location.reload();
          });
        });
      } else {
        // Service worker found, proceed normally
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('[ServiceWorker] No internet connection. App running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
        console.log('[ServiceWorker] Unregistered');
      })
      .catch((error) => {
        console.error('[ServiceWorker] Unregistration failed:', error);
      });
  }
}

/**
 * Check for service worker updates manually
 */
export function checkForUpdates() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CHECK_UPDATES'
    });
  }
}

/**
 * Clear all caches
 */
export async function clearCaches() {
  const controller = navigator.serviceWorker?.controller;
  
  if ('serviceWorker' in navigator && controller) {
    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        if (event.data.success) {
          resolve(true);
        } else {
          reject(new Error('Failed to clear caches'));
        }
      };
      
      controller.postMessage(
        { type: 'CLEAR_CACHE' },
        [messageChannel.port2]
      );
    });
  }
  
  throw new Error('Service worker not available');
}
